import React, { useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Field, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import InputField from 'components/InputField';
import RadioSliderField from 'components/RadioSliderField';
import useFirstRender from 'hooks/useFirstRender';
import useMatchMedia from 'hooks/useMatchMedia';
import useOperationStepValues from 'router/data/useOperationStepValues';
import validations from 'validations';
import { getFieldValidations } from 'validations/general';
import { Listing } from 'models/Listing';
import { WhenTwoFieldChanges } from '../../../../components/WhenTwoFieldChanges';
import {
  calculatePriceDifference,
  calculatePriceDivision,
} from '../../../../utils';
import {
  ASKING_PRICE_SELECT,
  AskingPriceSelect,
  STOCK_AT_VALUE,
  StockAtValue,
} from 'models/DealRoom';
import { useGenerateSliderOptions } from '../../../../hooks/useGenerateSliderOptions';
import useListingFieldIsRequired from '../../../../hooks/useListingFieldIsRequired';

import './ListingFinancialsEditor.styles.scss';

export type ListingFinancialsEditorProps = {
  className?: string;
  /**
   * Urgent listings have their own Statement of Information holding only
   * revenue, EBITDA and averageStockValue. Rendering the price guide, the
   * amounts or the stock-at-value selector would edit fields that flow never
   * sets, and the derived recompute would clear grossProfit and friends, since
   * COGS and lessOverheads do not exist there.
   */
  isUrgent?: boolean;
  /**
   * Fired when one control is committed - money inputs on blur, the two
   * sliders on change. Shape matches what patchListingValue takes in the form
   * view, e.g. { revenue: '1,250,000' }.
   */
  onSave?: (
    patch: Partial<Listing>,
    isValid: boolean,
    options?: { debounce?: boolean },
  ) => void;
};

/**
 * Edit half of the preview-mode financial block: one panel covering price
 * guide, revenue, EBITDA and stock at valuation, including the two type
 * selectors. Field configuration mirrors StatementOfInformation so both views
 * validate and store identically.
 */
const ListingFinancialsEditor: React.FC<ListingFinancialsEditorProps> = (
  props,
) => {
  const { className, isUrgent = false, onSave } = props;

  const { t } = useTranslation();
  const { priceGuideOptions } = useOperationStepValues();
  const { stockAtValueOptions } = useGenerateSliderOptions();

  const isPhablet = useMatchMedia('isPhablet');

  /**
   * The full labels wrap out of the slider once the block stacks. The
   * "Stock at value" heading above carries the context, so the short forms
   * stay unambiguous.
   */
  const stockOptions = useMemo(
    () =>
      isPhablet
        ? [
            {
              label: t('included'),
              value: STOCK_AT_VALUE.INCLUDE_STOCK_AT_VALUE,
            },
            {
              label: t('excluded'),
              value: STOCK_AT_VALUE.EXCLUDE_STOCK_AT_VALUE,
            },
            { label: t('noStock'), value: STOCK_AT_VALUE.NO_STOCK_AT_VALUE },
          ]
        : stockAtValueOptions,
    [isPhablet, stockAtValueOptions, t],
  );

  const {
    values: {
      askingPriceSelect,
      stockAtValue,
      grossProfit,
      operatingProfit,
      EBITDAMultiple,
    },
  } = useFormState<Partial<Listing>>({ subscription: { values: true } });

  /**
   * Statement of Information derives these from revenue / amount / EBITDA, and
   * the buyer-facing teaser renders grossProfit and EBITDAMultiple. Editing the
   * inputs here without sending the derived values would leave a live listing
   * showing figures that contradict each other.
   */
  const derivedValues = useMemo(
    () =>
      isUrgent ? {} : { grossProfit, operatingProfit, EBITDAMultiple },
    [isUrgent, grossProfit, operatingProfit, EBITDAMultiple],
  );

  const isFixedPrice = askingPriceSelect === ASKING_PRICE_SELECT.FIXED_PRICE;
  const hasStockAtValue = stockAtValue !== STOCK_AT_VALUE.NO_STOCK_AT_VALUE;

  const [isAmountRequired] = useListingFieldIsRequired('amount');
  const [isAmountFromRequired] = useListingFieldIsRequired('amountFrom');
  const [isAmountToRequired] = useListingFieldIsRequired('amountTo');
  const [isRevenueRequired] = useListingFieldIsRequired('revenue');
  const [isEBITDARequired] = useListingFieldIsRequired('EBITDA');
  const [isAverageStockValueRequired] =
    useListingFieldIsRequired('averageStockValue');

  const commit = useCallback(
    (key: keyof Listing) =>
      (e: React.FocusEvent<HTMLInputElement>, isValid: boolean) =>
        onSave?.(
          { [key]: e.target.value, ...derivedValues } as Partial<Listing>,
          isValid,
        ),
    [derivedValues, onSave],
  );

  const isFirstRender = useFirstRender();

  /**
   * Catches recomputes that land after a commit - switching the price type
   * changes which amount feeds EBITDAMultiple. Debounced so a recompute on
   * every keystroke merges into the blur save rather than sending on its own.
   */
  useEffect(() => {
    if (isFirstRender || isUrgent) return;

    onSave?.(derivedValues as Partial<Listing>, true, { debounce: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedValues]);

  const classes = classNames('listing-financials-editor', className);

  return (
    <div className={classes}>
      {!isUrgent && (
        <>
          {/* Same derivations the form view applies, so both write the same set. */}
          <WhenTwoFieldChanges
            name="grossProfit"
            nameFrom="revenue"
            nameTo="COGS"
            calculateNewValue={calculatePriceDifference}
          />
          <WhenTwoFieldChanges
            name="operatingProfit"
            nameFrom="grossProfit"
            nameTo="lessOverheads"
            calculateNewValue={calculatePriceDifference}
          />
          <WhenTwoFieldChanges
            name="EBITDAMultiple"
            nameFrom={isFixedPrice ? 'amount' : 'amountFrom'}
            nameTo="EBITDA"
            calculateNewValue={calculatePriceDivision}
          />

          <Field
            name="askingPriceSelect"
            label={t('priceGuide')}
            slideSteps={priceGuideOptions}
            component={RadioSliderField}
            customOnChange={(askingPriceSelect: AskingPriceSelect) =>
              onSave?.({ askingPriceSelect, ...derivedValues }, true)
            }
          />
        </>
      )}

      {isUrgent ? null : isFixedPrice ? (
        <Field
          name="amount"
          label={t('salePrice')}
          numberStyle="dottedNumber"
          placeholder="$"
          component={InputField}
          customOnBlur={commit('amount')}
          {...getFieldValidations(isAmountRequired, [
            validations.greatherThanZero(t('onlyPositiveNumbersAreAllowed')),
          ])}
        />
      ) : (
        <div className="listing-financials-editor__row">
          <Field
            name="amountFrom"
            label={t('amountFrom')}
            numberStyle="dottedNumber"
            placeholder="$"
            component={InputField}
            customOnBlur={commit('amountFrom')}
            {...getFieldValidations(isAmountFromRequired, [
              validations.positiveNumbersOnly(),
            ])}
          />
          <Field
            name="amountTo"
            label={t('amountTo')}
            numberStyle="dottedNumber"
            placeholder="$"
            component={InputField}
            customOnBlur={commit('amountTo')}
            {...getFieldValidations(isAmountToRequired, [
              validations.positiveNumbersOnly(),
              validations.isGreatherOrEqualThanField('amountFrom'),
            ])}
          />
        </div>
      )}

      {!isUrgent &&
        askingPriceSelect === ASKING_PRICE_SELECT.EXPRESSION_OF_INTEREST && (
          <p className="eoi-description">{t('askingPriceEoiDescription')}</p>
        )}

      <div className="listing-financials-editor__row">
        <Field
          name="revenue"
          label={t(isUrgent ? 'revenue' : 'revenueUC')}
          numberStyle="dottedNumber"
          placeholder="$"
          component={InputField}
          customOnBlur={commit('revenue')}
          {...getFieldValidations(
            isRevenueRequired,
            isUrgent
              ? [validations.positiveNumbersOnly()]
              : [
                  validations.positiveNumbersOnly(),
                  validations.isGreatherOrEqualThanField('COGS'),
                ],
          )}
        />
        <Field
          name="EBITDA"
          label={t('ebitda')}
          numberStyle="dottedNumber"
          placeholder="$"
          component={InputField}
          customOnBlur={commit('EBITDA')}
          {...getFieldValidations(
            isEBITDARequired,
            isUrgent
              ? [validations.positiveNumbersOnly()]
              : [
                  validations.validDecimalNumbersOnlyValidation(
                    t('numbersOnlyError'),
                  ),
                ],
          )}
        />
      </div>

      {!isUrgent && (
        <Field
          name="stockAtValue"
          label={t('stockAtValue')}
          slideSteps={stockOptions}
          component={RadioSliderField}
          validate={validations.required()}
          customOnChange={(stockAtValue: StockAtValue) =>
            onSave?.({ stockAtValue, ...derivedValues }, true)
          }
        />
      )}

      {(isUrgent || hasStockAtValue) && (
        <Field
          name="averageStockValue"
          label={t('averageStockValue')}
          numberStyle="dottedNumber"
          placeholder="$"
          component={InputField}
          customOnBlur={commit('averageStockValue')}
          {...getFieldValidations(isAverageStockValueRequired, [
            validations.positiveNumbersOnly(),
          ])}
        />
      )}
    </div>
  );
};

export default ListingFinancialsEditor;
